/**
 * @file Web Gallery Album Folder related queries.
 */

import gql from 'graphql-tag';

import {
  WebGalleryAlbumFolderChildrenDetails,
  WebGalleryAlbumFolderDetails,
} from '../fragments/album-folder';
import { WebGalleryAccountDetailsWithImageAssets } from '../fragments/account';
import { BlogPostListingDetails } from '../fragments/blog';
import { WebGalleryAlbumCustomPrintDetails } from '../fragments/custom-prints';
import { VideoDetails } from '../fragments/video';
import { AutoContributorSettingsDetails } from '../../../../graphql/fragments/contributor-assignments';
import {
  AlbumFolderEventSettingsFields,
  albumFolderPhotoPricingDetails,
  albumFolderPrintPackageDetails,
} from '../../../../graphql/fragments/album-folder';
import { albumCustomPrintDetails } from '../../../../graphql/fragments/album-custom-print';

export const AlbumFolderForWebGalleries = gql`
  query albumFolderForWebGalleries($id: RecordID!) {
    albumFolder(id: $id) {
      ...WebGalleryAlbumFolderDetails
      ...WebGalleryAlbumFolderChildrenDetails
      album_custom_prints(slice: { limit: 1 }, custom_print_types: [COLLAGE]) {
        records {
          ...WebGalleryAlbumCustomPrintDetails
        }
      }
      album_photos_count
      account {
        ...WebGalleryAccountDetailsWithImageAssets
      }
      root {
        ...WebGalleryAlbumFolderDetails
        auto_contributor_share_url
        auto_contributor_settings {
          ...AutoContributorSettingsDetails
        }
      }

      auto_contributor_share_url
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      account_has_onboarded
      all_photos_mode
      purchased_event_addon_types
      event_settings {
        ...AlbumFolderEventSettingsFields
      }
    }
  }
  ${WebGalleryAlbumFolderDetails}
  ${WebGalleryAlbumFolderChildrenDetails}
  ${WebGalleryAccountDetailsWithImageAssets}
  ${WebGalleryAlbumCustomPrintDetails}
  ${AutoContributorSettingsDetails}
  ${AlbumFolderEventSettingsFields}
`;

export const AlbumFolderWithBlogPosts = gql`
  query albumFolderWithBlogPosts(
    $id: RecordID!
    $limit: Int = 20
    $offset: Int = 0
  ) {
    albumFolder(id: $id) {
      id
      name
      linked_blog_posts(slice: { limit: $limit, offset: $offset }) {
        total
        count
        records {
          ...BlogPostListingDetails
        }
      }
    }
  }
  ${BlogPostListingDetails}
`;

export const AlbumFolderWithVideos = gql`
  query albumFolderWithVideos(
    $id: RecordID!
    $limit: Int = 20
    $offset: Int = 0
  ) {
    albumFolder(id: $id) {
      id
      name
      account {
        id
      }
      videos(slice: { limit: $limit, offset: $offset }) {
        total
        records {
          ...VideoDetails
        }
      }
    }
  }
  ${VideoDetails}
`;

export const AlbumRootAlbumFolderPurchaseDetails = gql`
  fragment AlbumRootAlbumFolderPurchaseDetails on Album {
    id
    root_album_folder {
      id
      name
      join_code
      account_has_onboarded
      individual_albums
      price {
        value
      }
      album_type
      joins_enabled
      search_subscription_credits_joins_enabled
      is_demo
      hero_image {
        id
        url
      }
      is_web_gallery
      web_gallery_price_per_photo {
        value
        currency
      }
      auto_contributor_share_url
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      picture_day_enabled
      ...albumFolderPrintPackageDetails
      ...albumFolderPhotoPricingDetails
      album_custom_prints(
        slice: { limit: 99 }
        unique_custom_prints: true
        custom_print_types: [CUSTOM_PRINT]
        custom_print_subtype: null
      ) {
        total
        records {
          ...albumCustomPrintDetails
        }
      }
      collage_custom_prints: album_custom_prints(
        slice: { limit: 99 }
        unique_custom_prints: true
        custom_print_types: [PRINT_PRODUCT]
        custom_print_subtype: COLLAGE
      ) {
        total
        records {
          ...albumCustomPrintDetails
        }
      }
      account_card_album_custom_prints: album_custom_prints(
        slice: { limit: 99 }
        unique_custom_prints: true
        custom_print_types: [PRINT_PRODUCT]
        custom_print_subtype: null
      ) {
        total
        records {
          ...albumCustomPrintDetails
        }
      }
    }
  }
  ${AutoContributorSettingsDetails}
  ${albumFolderPrintPackageDetails}
  ${albumFolderPhotoPricingDetails}
  ${albumCustomPrintDetails}
`;

export const AlbumFolderForWebGalleryPurchasedPhotoDownloads = gql`
  query albumFolderForWebGalleryPurchasedPhotoDownloads($id: RecordID!) {
    albumFolder(id: $id) {
      id
      children {
        albums(slice: { limit: 9999 }) {
          total
          records {
            id
            purchased_photo_downloads {
              total
              records {
                id
                photo {
                  id
                }
              }
            }
          }
        }
        album_folders(slice: { limit: 9999 }) {
          total
          records {
            id
            name
            parent {
              id
            }
            children {
              albums(slice: { limit: 9999 }) {
                total
                records {
                  id
                  purchased_photo_downloads {
                    total
                    records {
                      id
                      photo {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const AlbumFolderForWebGalleryUpsellCheck = gql`
  query albumFolderForWebGalleryUpsellCheck($id: RecordID!) {
    albumFolder(id: $id) {
      id
      is_demo
      joins_enabled
      album_price_mode
      search_subscription_credits_joins_enabled
      price {
        value
      }
    }
  }
`;
